"use client";

import { useEffect, useRef, useState } from "react";
import {
  DIALOGUE,
  SCENE_DURATION,
  SILENT_DURATION,
  SPEAKERS,
  bindScene,
  renderScene,
  sceneMarkup,
  sceneTimeAtSilent,
  silentTimeAtScene,
} from "./_video/scene.js";
import styles from "./page.module.css";

// The same drawing, dialogue and timing as the HyperFrames composition in ./_video.
const MARKUP = sceneMarkup();
const SOUND_URL = "/member-unavailable/leviathan-bowlbot-dishes.m4a";
const DESCRIPTION = `Erlich and Jian-Yang at a table. Jian-Yang's Project Leviathan is under a cloth. Erlich thinks it is Trade With Titans' secret feature and hears BowlBot as BullBot, an AI that trades the bull market. He gives Jian-Yang his palapa and agrees to clean the sink for ten percent. It is a smart dishwasher Jian-Yang built for himself, it is not finished, and Erlich is left doing the dishes. ${DIALOGUE.map(
  (line) => `${SPEAKERS[line.speaker]}: ${line.text}`,
).join(" ")}`;

type Status = "waiting" | "playing" | "paused" | "ended";

type Player = {
  play: (from?: number) => void;
  pause: () => void;
  setSound: (on: boolean) => void;
  destroy: () => void;
};

/**
 * With sound off the scene runs on its own clock through a slower timeline
 * that waits on each speech bubble long enough to read it. With sound on the
 * drawing follows the audio, including while the first audio data is loading.
 * Turning sound on starts the conversation from the beginning.
 */
function createPlayer(
  host: HTMLElement,
  audio: HTMLAudioElement,
  onStatus: (status: Status) => void,
  onSoundBlocked: () => void,
): Player {
  const parts = bindScene(host);
  // Position in the muted run, and the scene time it shows.
  let silent = 0;
  let time = 0;
  let frame = 0;
  let last: number | null = null;
  let status: Status = "waiting";
  let sound = false;
  let resumeWhenVisible = false;
  let initialWaiting = true;
  let inView = false;
  let startTimer: ReturnType<typeof setTimeout> | null = null;
  let observer: IntersectionObserver | null = null;

  const cancelInitialStart = () => {
    if (startTimer !== null) clearTimeout(startTimer);
    startTimer = null;
  };

  const setStatus = (next: Status) => {
    status = next;
    onStatus(next);
  };

  const draw = () => renderScene(parts, time, sound ? time : silent);

  const startAudio = () => {
    audio.currentTime = time;
    audio.play().catch((error) => {
      if (error?.name === "AbortError") return;
      sound = false;
      silent = silentTimeAtScene(time);
      onSoundBlocked();
    });
  };

  const stop = () => {
    cancelAnimationFrame(frame);
    frame = 0;
    last = null;
  };

  const tick = (now: number) => {
    // Background tabs stop sending frames. A capped step resumes the story
    // where it was instead of jumping ahead.
    const step = last === null ? 0 : Math.min(0.05, (now - last) / 1000);
    last = now;
    let done: boolean;
    if (sound) {
      time = Math.min(SCENE_DURATION, audio.ended ? SCENE_DURATION : audio.currentTime);
      done = time >= SCENE_DURATION;
    } else {
      silent = Math.min(SILENT_DURATION, silent + step);
      time = sceneTimeAtSilent(silent);
      done = silent >= SILENT_DURATION;
    }
    draw();
    if (done) {
      stop();
      setStatus("ended");
      return;
    }
    frame = requestAnimationFrame(tick);
  };

  const play = (from?: number) => {
    cancelInitialStart();
    initialWaiting = false;
    observer?.disconnect();
    observer = null;
    if (from !== undefined) {
      time = from;
      silent = silentTimeAtScene(from);
    }
    stop();
    draw();
    setStatus("playing");
    if (sound) startAudio();
    frame = requestAnimationFrame(tick);
  };

  const pause = () => {
    cancelInitialStart();
    stop();
    audio.pause();
    setStatus("paused");
  };

  const scheduleInitialStart = () => {
    if (!initialWaiting || !inView || document.hidden || startTimer !== null) return;
    startTimer = setTimeout(() => {
      startTimer = null;
      if (initialWaiting && inView && !document.hidden) play(0);
    }, 1000);
  };

  const onVisibility = () => {
    if (initialWaiting) {
      if (document.hidden) cancelInitialStart();
      else scheduleInitialStart();
      return;
    }
    if (document.hidden && status === "playing") {
      resumeWhenVisible = true;
      pause();
    } else if (!document.hidden && resumeWhenVisible) {
      resumeWhenVisible = false;
      play();
    }
  };
  document.addEventListener("visibilitychange", onVisibility);

  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    // Reduced motion shows the finished picture. Replay still plays it on request.
    time = SCENE_DURATION;
    silent = SILENT_DURATION;
    initialWaiting = false;
    draw();
    setStatus("ended");
  } else {
    draw();
    observer = new IntersectionObserver(
      (entries) => {
        inView = entries.some((entry) => entry.isIntersecting);
        if (inView) scheduleInitialStart();
        else cancelInitialStart();
      },
      { threshold: 0.35 },
    );
    observer.observe(host);
  }

  return {
    play,
    pause,
    setSound(on) {
      sound = on;
      if (!on) {
        // Carry on reading from the same moment with the muted timing.
        audio.pause();
        silent = silentTimeAtScene(time);
        return;
      }
      // Let a member hear the complete exchange when they enable sound.
      play(0);
    },
    destroy() {
      cancelInitialStart();
      stop();
      audio.pause();
      observer?.disconnect();
      document.removeEventListener("visibilitychange", onVisibility);
    },
  };
}

function PauseIcon() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <rect x="4" y="3" width="2.6" height="10" rx="1" />
      <rect x="9.4" y="3" width="2.6" height="10" rx="1" />
    </svg>
  );
}

function PlayIcon() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <path d="M5 3.2v9.6a.6.6 0 0 0 .9.5l7.6-4.8a.6.6 0 0 0 0-1L5.9 2.7a.6.6 0 0 0-.9.5Z" />
    </svg>
  );
}

function ReplayIcon() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3.2 8a4.8 4.8 0 1 0 1.5-3.5" />
      <path d="M3.4 2.4v2.8h2.8" />
    </svg>
  );
}

function SoundIcon({ on }: { on: boolean }) {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2.8 6.2h2.1L8.2 3.4v9.2L4.9 9.8H2.8Z" fill="currentColor" stroke="none" />
      {on ? (
        <>
          <path d="M10.4 6a2.8 2.8 0 0 1 0 4" />
          <path d="M12.2 4.2a5.4 5.4 0 0 1 0 7.6" />
        </>
      ) : (
        <path d="m10.6 6.2 3.4 3.6m0-3.6-3.4 3.6" />
      )}
    </svg>
  );
}

export function WetPaintScene() {
  const hostRef = useRef<HTMLDivElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const playerRef = useRef<Player | null>(null);
  const [status, setStatus] = useState<Status>("waiting");
  const [sound, setSound] = useState(false);

  useEffect(() => {
    const host = hostRef.current;
    const audio = audioRef.current;
    if (!host || !audio) return;
    const player = createPlayer(host, audio, setStatus, () => setSound(false));
    playerRef.current = player;
    // The drawing fades in once it shows the right frame.
    host.dataset.ready = "true";
    return () => {
      player.destroy();
      playerRef.current = null;
    };
  }, []);

  const main =
    status === "playing"
      ? { label: "Pause", icon: <PauseIcon />, run: () => playerRef.current?.pause() }
      : status === "paused" || status === "waiting"
        ? { label: "Play", icon: <PlayIcon />, run: () => playerRef.current?.play() }
        : { label: "Replay", icon: <ReplayIcon />, run: () => playerRef.current?.play(0) };

  return (
    <div className={styles.stage}>
      <p className={styles.filmIntro}>What’s going on with Project Leviathan?</p>
      <div
        ref={hostRef}
        className={styles.art}
        data-ready="false"
        role="img"
        aria-label={DESCRIPTION}
        dangerouslySetInnerHTML={{ __html: MARKUP }}
      />
      <div className={styles.controls}>
        <button
          type="button"
          className={styles.control}
          aria-pressed={sound}
          onClick={() => {
            const next = !sound;
            setSound(next);
            playerRef.current?.setSound(next);
          }}
        >
          <SoundIcon on={sound} />
          <span>Sound</span>
        </button>
        <button type="button" className={`${styles.control} ${styles.playback}`} onClick={main.run}>
          {main.icon}
          <span>{main.label}</span>
        </button>
      </div>
      <audio ref={audioRef} src={SOUND_URL} preload="none" />
    </div>
  );
}
